import { Skeleton } from '@/components/ui/skeleton';
import { formatTicketDateShort } from '@/lib/ticket-datetime';
import { cn } from '@/lib/utils';
import { studentInitials } from '@/components/tickets/ticketLabels';

type StudentRecord = {
  fullName?: string;
  email?: string;
  phone?: string;
};

type StudentContext = {
  student?: StudentRecord | null;
  programName?: string | null;
  deliveryMode?: string | null;
  paymentPlan?: string | null;
  learningPlatform?: string | null;
  programStartDate?: string | Date | null;
  studentStatus?: string | null;
};

type TicketHistory = {
  totalTickets: number;
  openTickets: number;
};

function formatDetailDate(value: unknown): string {
  if (!value) return '—';
  return formatTicketDateShort(String(value));
}

function DetailCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-border bg-muted/40 p-3.5">
      <span className="mb-1.5 block text-[11px] font-bold tracking-wide text-muted-foreground uppercase">
        {label}
      </span>
      <span className="block text-sm leading-snug font-bold break-words text-foreground">{value}</span>
    </div>
  );
}

function StudentStatusBadge({ status }: { status: string }) {
  const completed = status.toLowerCase() === 'completed';
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <span className="mb-2 block text-[11px] font-bold tracking-wide text-muted-foreground uppercase">
        Student Current Status
      </span>
      <span
        className={cn(
          'inline-flex items-center gap-1.5 rounded-full px-2.5 py-2 text-xs font-bold',
          completed ? 'bg-info/10 text-info' : 'bg-success/10 text-success'
        )}
      >
        <span
          className={cn('h-2 w-2 shrink-0 rounded-full', completed ? 'bg-info' : 'bg-success')}
          aria-hidden
        />
        {status}
      </span>
    </div>
  );
}

export function StudentDetailsSkeleton() {
  return (
    <div className="flex h-full flex-col">
      <div className="border-b bg-background px-5 py-5">
        <div className="flex items-center justify-between gap-3">
          <Skeleton className="h-6 w-32 shrink-0" />
          <Skeleton className="h-[38px] w-28 shrink-0 rounded-full" />
        </div>
      </div>
      <div className="space-y-4 p-5">
        <div className="flex items-center gap-3.5">
          <Skeleton className="h-[54px] w-[54px] rounded-full" />
          <div className="space-y-2">
            <Skeleton className="h-5 w-36" />
            <Skeleton className="h-4 w-44" />
          </div>
        </div>
        <div className="space-y-3">
          {Array.from({ length: 7 }).map((_, i) => (
            <Skeleton key={i} className="h-[72px] w-full rounded-2xl" />
          ))}
        </div>
      </div>
    </div>
  );
}

type StudentDetailsPanelProps = {
  studentContext: StudentContext;
  ticketHistory?: TicketHistory;
  onViewAsUser: () => void;
};

export function StudentDetailsPanel({
  studentContext,
  ticketHistory,
  onViewAsUser,
}: StudentDetailsPanelProps) {
  const student = studentContext.student;
  const fullName = student?.fullName ?? '—';
  const email = student?.email ?? '—';
  const phone = student?.phone ?? '—';
  const studentStatus = studentContext.studentStatus ?? 'In Progress';

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <div className="border-b bg-background px-5 py-5">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-lg font-bold tracking-tight">Student Details</h2>
          <button
            type="button"
            onClick={onViewAsUser}
            className="h-[38px] shrink-0 rounded-full border border-primary/30 bg-background px-3.5 text-xs font-bold whitespace-nowrap text-primary shadow-[0_8px_20px_rgba(15,23,42,0.045)] transition-colors hover:bg-accent"
          >
            View as User
          </button>
        </div>
      </div>

      <div className="flex-1 space-y-4 p-5">
        <div className="flex items-center gap-3.5">
          <div
            className="grid h-[54px] w-[54px] shrink-0 place-items-center rounded-full bg-gradient-to-br from-primary-from to-primary-to text-base font-bold text-primary-foreground"
            aria-hidden
          >
            {studentInitials(fullName !== '—' ? fullName : undefined)}
          </div>
          <div className="min-w-0">
            <h3 className="truncate text-lg font-bold tracking-tight">{fullName}</h3>
            <p className="truncate text-sm text-muted-foreground">{email}</p>
          </div>
        </div>

        <div className="grid gap-3">
          <DetailCard label="Phone" value={phone} />
          <DetailCard label="Email" value={email} />
          <DetailCard label="Program Name" value={String(studentContext.programName ?? '—')} />
          <DetailCard label="Mode Name" value={String(studentContext.deliveryMode ?? '—')} />
          <DetailCard label="Payment Plan Name" value={String(studentContext.paymentPlan ?? '—')} />
          <DetailCard label="Learning Platform" value={String(studentContext.learningPlatform ?? '—')} />
          <StudentStatusBadge status={studentStatus} />
          <DetailCard
            label="Program Start Date"
            value={formatDetailDate(studentContext.programStartDate)}
          />
        </div>

        {ticketHistory && (
          <div className="rounded-[18px] border border-primary/20 bg-accent p-4">
            <h3 className="mb-2.5 text-sm font-bold">Ticket History</h3>
            <div className="grid grid-cols-2 gap-2.5">
              <div className="rounded-[14px] border border-primary/20 bg-card p-3">
                <strong className="block text-[22px] leading-none font-bold">
                  {ticketHistory.totalTickets}
                </strong>
                <span className="mt-1 block text-xs font-semibold text-muted-foreground">
                  Total tickets created
                </span>
              </div>
              <div className="rounded-[14px] border border-primary/20 bg-card p-3">
                <strong className="block text-[22px] leading-none font-bold">
                  {ticketHistory.openTickets}
                </strong>
                <span className="mt-1 block text-xs font-semibold text-muted-foreground">
                  Currently open
                </span>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
